/**
 * Formatting helpers for the events list and the event workspace: date-range
 * formatting, event-time range formatting, and timestamps.
 *
 * Money formatting is not defined here — it lives in @shared/nz-money
 * (formatNZD for two-decimal, formatNZDCompact for whole/compact); import
 * those directly. The old `formatCurrency` alias is gone (sweep 2 Pass D).
 */

export function formatDate(
  dateStr: string,
  style: "short" | "long" = "long",
): string {
  return new Date(dateStr).toLocaleDateString("en-NZ", {
    weekday: style,
    day: "numeric",
    month: style === "short" ? "short" : "long",
    year: "numeric",
  });
}

/**
 * Format a date range for display.
 * Single day: "Wed, 12 Mar 2026" (short)
 * Same month: "12-14 Mar 2026"
 * Cross month: "28 Mar - 1 Apr 2026"
 * Cross year: "30 Dec 2026 - 2 Jan 2027"
 */
export function formatDateRange(
  startStr: string,
  endStr?: string | null,
  style: "short" | "long" = "long",
): string {
  if (!endStr) return formatDate(startStr, style);

  const start = new Date(startStr);
  const end = new Date(endStr);

  if (start.toDateString() === end.toDateString())
    return formatDate(startStr, style);

  const monthStyle = style === "short" ? "short" : "long";

  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    const month = end.toLocaleDateString("en-NZ", { month: monthStyle });
    return `${start.getDate()}-${end.getDate()} ${month} ${end.getFullYear()}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    const startPart = start.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: monthStyle,
    });
    const endPart = end.toLocaleDateString("en-NZ", {
      day: "numeric",
      month: monthStyle,
      year: "numeric",
    });
    return `${startPart} - ${endPart}`;
  }

  const startFull = start.toLocaleDateString("en-NZ", {
    day: "numeric",
    month: monthStyle,
    year: "numeric",
  });
  const endFull = end.toLocaleDateString("en-NZ", {
    day: "numeric",
    month: monthStyle,
    year: "numeric",
  });
  return `${startFull} - ${endFull}`;
}

/** Convert a time string to HH:mm (24-hour), or "" if unparseable.
 *  Exported (#83): the event workspace needs this beyond formatTimeRange. */
export function toTimeValue(time: string | null | undefined): string {
  if (!time || !time.trim()) return "";
  const t = time.trim();

  if (/^\d{1,2}:\d{2}$/.test(t) && !t.toLowerCase().includes("m")) {
    const [h, m] = t.split(":").map(Number);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }

  const match12 = t.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const mins = parseInt(match12[2] || "0", 10);
    const period = match12[3].toLowerCase();
    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  }

  return "";
}

/**
 * Format a free-text event time string, flagging end times that cross
 * midnight with a "(next day)" suffix. Unparseable inputs pass through.
 */
export function formatTimeRange(time: string | null | undefined): string {
  if (!time) return "";
  const trimmed = time.trim();
  if (!trimmed) return "";

  const parts = trimmed.split(/\s*[-–]\s*/);
  if (parts.length !== 2) return trimmed;

  const startNorm = toTimeValue(parts[0]);
  const endNorm = toTimeValue(parts[1]);
  if (!startNorm || !endNorm) return trimmed;

  const [sh, sm] = startNorm.split(":").map(Number);
  const [eh, em] = endNorm.split(":").map(Number);
  if (eh * 60 + em >= sh * 60 + sm) return trimmed;

  return `${trimmed} (next day)`;
}

// ─── Workspace additions (#83) ──────────────────────────────────────────────
// The event workspace needs a wider slice of format-utils than the events
// list.

/** "Sat 16 Apr, 10:54 am" — drops the year when it matches the current year. */
export function formatTimestamp(dateStr: string): string {
  const d = new Date(dateStr);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const datePart = d.toLocaleDateString("en-NZ", {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const timePart = d
    .toLocaleTimeString("en-NZ", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
  return `${datePart}, ${timePart}`;
}

/** "16 Mar 2026, 10:54 am" — used for the cancellation banner. */
export function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-NZ", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Inclusive day count between two dates. Returns 1 for single-day events. */
export function getEventDayCount(
  startStr: string,
  endStr?: string | null,
): number {
  if (!endStr) return 1;
  const start = new Date(startStr);
  const end = new Date(endStr);
  const diffMs = end.getTime() - start.getTime();
  return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
}

/**
 * Inclusive hire-window day count from a booking item's starts_on/ends_on,
 * floor 1 — mirrors Go bookingItemDays. This window, not the row's `days`
 * field, is what the server prices from (#227).
 */
export function bookingItemDays(
  startsOn?: string | null,
  endsOn?: string | null,
): number {
  if (!startsOn || !endsOn) return 1;
  const s = Date.parse(`${startsOn}T00:00:00Z`);
  const e = Date.parse(`${endsOn}T00:00:00Z`);
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return 1;
  const days = Math.round((e - s) / 86_400_000) + 1;
  return days < 1 ? 1 : days;
}

/**
 * ISO date + (days - 1), clamped at the start — mirrors addDaysISO in
 * go-service/internal/api/gear_items.go. Inverse of bookingItemDays.
 */
export function addDaysISO(startISO: string, days: number): string {
  const t = Date.parse(`${startISO}T00:00:00Z`);
  if (Number.isNaN(t)) return startISO;
  const offset = days > 1 ? days - 1 : 0;
  return new Date(t + offset * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The extra-day fraction used when a Company's own rate is unavailable.
 * Mirrors defaultExtraDayRate in go-service/internal/api/generate_quote_from_gear.go.
 */
export const DEFAULT_EXTRA_DAY_RATE = 0.5;

/**
 * Multi-day gear pricing: day 1 = 100%, each subsequent day charges
 * extraDayRate. Mirrors multiDayGearRate in
 * go-service/internal/api/generate_quote_from_gear.go — keep both in sync.
 *
 * extraDayRate is the Company's `multi_day_extra_day_percent / 100`, shipped
 * on the workspace payload as `multiDayExtraDayPercent`. It used to be
 * hardcoded at 0.5 here while the server read the per-Company setting, so any
 * Company not on 50% saw editor line totals that disagreed with the generated
 * quote (#77).
 */
export function multiDayGearRate(
  days: number,
  extraDayRate: number = DEFAULT_EXTRA_DAY_RATE,
): number {
  if (days <= 1) return 1;
  return 1 + (days - 1) * normalizeExtraDayRate(extraDayRate);
}

/**
 * Guards the same range the server does (`companyExtraDayRate`): a percent
 * outside 0–100 falls back to the historic 50% rather than pricing wildly.
 */
function normalizeExtraDayRate(rate: number): number {
  if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
    return DEFAULT_EXTRA_DAY_RATE;
  }
  return rate;
}

/** Gear line total. Labour/transport pass through; gear uses multi-day rule. */
export function calcGearLineTotal(
  unitRate: number,
  quantity: number,
  days: number,
  isGear: boolean,
  extraDayRate: number = DEFAULT_EXTRA_DAY_RATE,
): number {
  if (!isGear) return unitRate * quantity * days;
  return unitRate * quantity * multiDayGearRate(days, extraDayRate);
}

/** Convert HH:mm (24h) to display "2pm" / "2:30pm". */
export function formatTimeDisplay(time: string | null | undefined): string {
  if (!time || !time.trim()) return "";
  const normalized = toTimeValue(time);
  if (!normalized) return time || "";

  const [h, m] = normalized.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  let displayHour = h % 12;
  if (displayHour === 0) displayHour = 12;

  return m === 0
    ? `${displayHour}${period}`
    : `${displayHour}:${String(m).padStart(2, "0")}${period}`;
}
